import { useTranslation } from 'react-i18next';
import { ISettingsType } from 'common/src/types';
import { Box, Typography, alpha, useTheme, Chip, Stack, LinearProgress, Divider } from '@mui/material';
import { Payment, Warning, Schedule, CalendarToday, Lock, CheckCircle, Cancel, ErrorOutline, Payments } from '@mui/icons-material';

interface PaymentStatusCardProps {
    settings: ISettingsType;
}


const getDaysUntilDue = (date: Date | number | null | undefined): number | null => {
    if (!date) return null;

    const dueDate = typeof date === 'number' ? new Date(date) : new Date(date);
    const today = new Date();

    const dueDateUTC = Date.UTC(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    const todayUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());

    const diffTime = dueDateUTC - todayUTC;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const formatCurrency = (value: string | number | null | undefined): string => {
    if (!value && value !== 0) return 'R$ 0,00';
    let num: number;
    if (typeof value === 'string') {
        const cleaned = value.replace(/[^0-9,.-]/g, '').replace(',', '.');
        num = parseFloat(cleaned);
    } else {
        num = value;
    }
    if (isNaN(num)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num);
};

const formatDate = (timestamp: number | null | undefined): string => {
    if (!timestamp) return 'Não definido';
    const date = new Date(timestamp);
    return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

export function PaymentStatusCard({ settings }: PaymentStatusCardProps) {
    const { t } = useTranslation();
    const theme = useTheme();

    const payment = settings?.payment || {};
    const appName = settings?.appName || 'App';

    const daysUntilClosing = getDaysUntilDue(payment.dueDateClosing ?? null);
    const daysUntilBlock = getDaysUntilDue(payment.dueDateClosingEnd ?? null);

    const isBlocked = payment?.dueDateClosingEnd ? new Date(payment.dueDateClosingEnd).getTime() <= Date.now() : false;
    const isExpiringSoon = daysUntilBlock !== null && daysUntilBlock > 0 && daysUntilBlock <= 5;
    const isDueSoon = daysUntilClosing !== null && daysUntilClosing > 0 && daysUntilClosing <= 3;

    const getStatusColor = () => {
        if (isBlocked) return theme.palette.error;
        if (isExpiringSoon) return theme.palette.warning;
        if (isDueSoon) return theme.palette.info;
        return theme.palette.success;
    };

    const getStatusIcon = () => {
        if (isBlocked) return <Cancel sx={{ fontSize: 20 }} />;
        if (isExpiringSoon) return <Warning sx={{ fontSize: 20 }} />;
        if (isDueSoon) return <Schedule sx={{ fontSize: 20 }} />;
        return <CheckCircle sx={{ fontSize: 20 }} />;
    };

    const getStatusText = () => {
        if (isBlocked) return t('PAYMENT_STATUS_CARD_STATUS_BLOCKED');
        if (isExpiringSoon) return t('PAYMENT_STATUS_CARD_STATUS_EXPIRING');
        if (isDueSoon) return t('PAYMENT_STATUS_CARD_STATUS_CLOSING');
        return t('PAYMENT_STATUS_CARD_STATUS_OK');
    };

    const getProgressValue = () => {
        if (daysUntilBlock === null) return 100;
        if (isBlocked) return 100;
        const progress = Math.min(100, (daysUntilBlock / 30) * 100);
        return Math.max(0, progress);
    };

    const getProgressColor = () => {
        if (isBlocked) return 'error';
        if (isExpiringSoon) return 'warning';
        if (isDueSoon) return 'info';
        return 'success';
    };

    const hasPaymentData = payment.price || payment.dueDateClosing || payment.dueDateClosingEnd;

    if (!hasPaymentData) {
        return (
            <Box
                sx={{
                    p: 4,
                    borderRadius: 4,
                    overflow: 'hidden',
                    position: 'relative',
                    backdropFilter: 'blur(20px)',
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    '&::before': {
                        content: '""',
                        top: -200,
                        width: 500,
                        height: 500,
                        right: -200,
                        borderRadius: '50%',
                        position: 'absolute',
                        background: `radial-gradient(circle, ${alpha(theme.palette.grey[500], 0.1)} 0%, transparent 70%)`,
                    },
                    '&::after': {
                        content: '""',
                        left: -100,
                        width: 300,
                        height: 300,
                        bottom: -100,
                        borderRadius: '50%',
                        position: 'absolute',
                        background: `radial-gradient(circle, ${alpha(theme.palette.grey[700], 0.08)} 0%, transparent 70%)`,
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, position: 'relative', zIndex: 1 }}>
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 3,
                            display: 'flex',
                            color: theme.palette.grey[300],
                            bgcolor: alpha(theme.palette.grey[500], 0.15),
                            border: `1px solid ${alpha(theme.palette.grey[500], 0.1)}`,
                        }}
                    >
                        <Payment sx={{ fontSize: 32 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight={700} sx={{ color: theme.palette.grey[200] }}>
                            {t('PAYMENT_STATUS_CARD_STATUS')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.grey[400] }}>
                            {t('PAYMENT_STATUS_CARD_NO_DATA')}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    }

    const statusColor = getStatusColor();
    const isUrgent = isBlocked || isExpiringSoon;

    return (
        <Box
            sx={{
                p: 4,
                borderRadius: 4,
                overflow: 'hidden',
                position: 'relative',
                backdropFilter: 'blur(20px)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                animation: isBlocked ? 'blockPulse 2s ease-in-out infinite' : 'none',
                bgcolor: isBlocked ? alpha(theme.palette.error.dark, 0.6) : isExpiringSoon ? alpha(theme.palette.warning.dark, 0.4) : alpha(theme.palette.background.paper, 0.7),
                boxShadow: isBlocked ? `0 0 80px ${alpha(theme.palette.error.main, 0.3)}, inset 0 0 100px ${alpha(theme.palette.error.main, 0.05)}` : isExpiringSoon ? `0 0 60px ${alpha(theme.palette.warning.main, 0.2)}, inset 0 0 80px ${alpha(theme.palette.warning.main, 0.03)}` : `0 20px 60px ${alpha(theme.palette.common.black, 0.3)}`,
                '@keyframes blockPulse': {
                    '0%': {
                        boxShadow: `0 0 80px ${alpha(theme.palette.error.main, 0.3)}, inset 0 0 100px ${alpha(theme.palette.error.main, 0.05)}`
                    },
                    '50%': {
                        boxShadow: `0 0 120px ${alpha(theme.palette.error.main, 0.5)}, inset 0 0 140px ${alpha(theme.palette.error.main, 0.1)}`
                    },
                    '100%': {
                        boxShadow: `0 0 80px ${alpha(theme.palette.error.main, 0.3)}, inset 0 0 100px ${alpha(theme.palette.error.main, 0.05)}`
                    },
                },
                '&::before': {
                    content: '""',
                    top: -250,
                    width: 600,
                    height: 600,
                    right: -250,
                    borderRadius: '50%',
                    position: 'absolute',
                    animation: isUrgent ? 'glowMove 6s ease-in-out infinite' : 'none',
                    background: `radial-gradient(circle, ${alpha(statusColor.main, isUrgent ? 0.25 : 0.08)} 0%, transparent 70%)`,
                },
                '&::after': {
                    content: '""',
                    left: -150,
                    width: 400,
                    height: 400,
                    bottom: -150,
                    borderRadius: '50%',
                    position: 'absolute',
                    animation: isUrgent ? 'glowMove 6s ease-in-out infinite 3s' : 'none',
                    background: `radial-gradient(circle, ${alpha(statusColor.main, isUrgent ? 0.15 : 0.04)} 0%, transparent 70%)`,
                },
                '@keyframes glowMove': {
                    '0%': { transform: 'translate(0, 0) scale(1)', opacity: 0.6 },
                    '33%': { transform: 'translate(-20px, 20px) scale(1.1)', opacity: 1 },
                    '66%': { transform: 'translate(20px, -20px) scale(0.9)', opacity: 0.8 },
                    '100%': { transform: 'translate(0, 0) scale(1)', opacity: 0.6 },
                }
            }}
        >
            {isBlocked && (
                <Box
                    sx={{
                        inset: 0,
                        opacity: 0.05,
                        position: 'absolute',
                        animation: 'stripeMove 20s linear infinite',
                        backgroundImage: `
                            repeating-linear-gradient(45deg, ${theme.palette.error.main} 0px, ${theme.palette.error.main} 2px, transparent 2px, transparent 8px),
                            repeating-linear-gradient(-45deg, ${theme.palette.error.main} 0px, ${theme.palette.error.main} 2px, transparent 2px, transparent 8px)
                        `,
                        '@keyframes stripeMove': {
                            '0%': { backgroundPosition: '0 0' },
                            '100%': { backgroundPosition: '40px 40px' },
                        }
                    }}
                />
            )}

            {isBlocked && (
                <Box
                    sx={{
                        top: '50%',
                        right: -40,
                        opacity: 0.04,
                        fontSize: 200,
                        fontWeight: 900,
                        userSelect: 'none',
                        letterSpacing: -20,
                        whiteSpace: 'nowrap',
                        position: 'absolute',
                        color: theme.palette.error.main,
                        transform: 'translateY(-50%) rotate(-15deg)',
                    }}
                >
                    {t('PAYMENT_STATUS_CARD_STATUS_BLOCKED')}
                </Box>
            )}

            <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                p: 1.5,
                                borderRadius: 3,
                                display: 'flex',
                                color: statusColor.main,
                                bgcolor: alpha(statusColor.main, isUrgent ? 0.25 : 0.1),
                                border: `1px solid ${alpha(statusColor.main, isUrgent ? 0.3 : 0.1)}`,
                                boxShadow: `0 0 40px ${alpha(statusColor.main, isUrgent ? 0.2 : 0.05)}`,
                            }}
                        >
                            <Payment sx={{ fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography
                                variant="h6"
                                fontWeight={800}
                                sx={{
                                    color: isBlocked ? theme.palette.error.main : theme.palette.text.primary,
                                    letterSpacing: isBlocked ? '0.5px' : 'normal',
                                }}
                            >
                                {t('PAYMENT_STATUS_CARD_STATUS')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                                {appName}
                            </Typography>
                        </Box>
                    </Box>
                    <Chip
                        label={getStatusText()}
                        icon={getStatusIcon() as any}
                        color={getProgressColor() as any}
                        sx={{
                            px: 1,
                            py: 2,
                            fontWeight: 800,
                            fontSize: '0.7rem',
                            letterSpacing: '0.5px',
                            '& .MuiChip-icon': { color: 'inherit' },
                            boxShadow: `0 4px 20px ${alpha(statusColor.main, isUrgent ? 0.4 : 0.2)}`,
                            animation: isBlocked ? 'chipBlink 1.5s ease-in-out infinite' : 'none',
                            border: isBlocked ? `2px solid ${alpha(theme.palette.error.main, 0.5)}` : 'none',
                            '@keyframes chipBlink': {
                                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                                '50%': { opacity: 0.7, transform: 'scale(0.95)' },
                            },
                        }}
                    />
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, letterSpacing: '0.3px' }}>
                            {t('PAYMENT_STATUS_CARD_CYCLE')}
                        </Typography>
                        <Typography
                            variant="caption"
                            fontWeight={800}
                            sx={{
                                fontSize: '0.75rem',
                                letterSpacing: '0.3px',
                                color: isBlocked ? theme.palette.error.main : statusColor.main,
                            }}
                        >
                            {isBlocked
                                ? t('PAYMENT_STATUS_CARD_BLOCKED_FOR_DAYS', { days: Math.abs(daysUntilBlock!) })
                                : daysUntilBlock !== null
                                    ? t('PAYMENT_STATUS_CARD_DAYS_REMAINING', { days: daysUntilBlock })
                                    : t('PAYMENT_STATUS_CARD_NA')}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={getProgressValue()}
                        color={getProgressColor() as any}
                        sx={{
                            height: 10,
                            borderRadius: 5,
                            bgcolor: alpha(statusColor.main, 0.08),
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 5,
                                boxShadow: `0 0 30px ${alpha(statusColor.main, isUrgent ? 0.5 : 0.2)}`,
                            }
                        }}
                    />
                </Box>

                <Divider sx={{ mb: 3, borderColor: alpha(statusColor.main, isUrgent ? 0.15 : 0.05) }} />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
                    <Box
                        sx={{
                            p: 2.5,
                            flex: 1,
                            borderRadius: 3,
                            transition: 'transform 0.2s ease',
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                            boxShadow: `inset 0 0 40px ${alpha(theme.palette.primary.main, 0.03)}`,
                            '&:hover': {
                                transform: 'translateY(-2px)',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Payments sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, letterSpacing: '0.3px' }}>
                                {t('PAYMENT_STATUS_CARD_AMOUNT_PAID')}
                            </Typography>
                        </Box>
                        <Typography variant="h3" fontWeight={800} sx={{ color: theme.palette.primary.main }}>
                            {formatCurrency(payment.price)}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            p: 2.5,
                            flex: 1,
                            borderRadius: 3,
                            transition: 'transform 0.2s ease',
                            bgcolor: alpha(theme.palette.info.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                            boxShadow: `inset 0 0 40px ${alpha(theme.palette.info.main, 0.03)}`,
                            '&:hover': {
                                transform: 'translateY(-2px)',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <CalendarToday sx={{ fontSize: 18, color: theme.palette.info.main }} />
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, letterSpacing: '0.3px' }}>
                                {t('PAYMENT_STATUS_CARD_CLOSING')}
                            </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight={700}>
                            {payment.dueDateClosing ? formatDate(payment.dueDateClosing) : t('PAYMENT_STATUS_CARD_NOT_DEFINED')}
                        </Typography>
                        {daysUntilClosing !== null && (
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 700,
                                    color: daysUntilClosing <= 0 ? theme.palette.error.main : theme.palette.text.secondary,
                                }}
                            >
                                {daysUntilClosing <= 0 ? t('PAYMENT_STATUS_CARD_OVERDUE') : t('PAYMENT_STATUS_CARD_DAYS_LEFT', { days: daysUntilClosing })}
                            </Typography>
                        )}
                    </Box>

                    <Box
                        sx={{
                            p: 2.5,
                            flex: 1,
                            borderRadius: 3,
                            transition: 'transform 0.2s ease',
                            boxShadow: `inset 0 0 40px ${alpha(statusColor.main, 0.03)}`,
                            bgcolor: isBlocked ? alpha(theme.palette.error.main, 0.12) : isExpiringSoon ? alpha(theme.palette.warning.main, 0.08) : alpha(theme.palette.warning.main, 0.04),
                            border: `1px solid ${isBlocked ? alpha(theme.palette.error.main, 0.3) : isExpiringSoon ? alpha(theme.palette.warning.main, 0.2) : alpha(theme.palette.warning.main, 0.08)}`,
                            '&:hover': {
                                transform: 'translateY(-2px)',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Lock sx={{ fontSize: 18, color: isBlocked ? theme.palette.error.main : theme.palette.warning.main }} />
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, letterSpacing: '0.3px' }}>
                                {t('PAYMENT_STATUS_CARD_BLOCK')}
                            </Typography>
                        </Box>
                        <Typography
                            variant="body1"
                            fontWeight={700}
                            sx={{ color: isBlocked ? theme.palette.error.main : isExpiringSoon ? theme.palette.warning.main : theme.palette.text.primary }}
                        >
                            {payment.dueDateClosingEnd ? formatDate(payment.dueDateClosingEnd) : t('PAYMENT_STATUS_CARD_NOT_DEFINED')}
                        </Typography>
                        {daysUntilBlock !== null && (
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 800,
                                    color: isBlocked ? theme.palette.error.main : isExpiringSoon ? theme.palette.warning.main : theme.palette.text.secondary,
                                }}
                            >
                                {isBlocked
                                    ? t('PAYMENT_STATUS_CARD_BLOCKED_ALERT_DAYS', { days: Math.abs(daysUntilBlock) })
                                    : t('PAYMENT_STATUS_CARD_DAYS_REMAINING', { days: daysUntilBlock })}
                            </Typography>
                        )}
                    </Box>
                </Stack>

                {isBlocked && (
                    <Box
                        sx={{
                            p: 3,
                            mt: 3,
                            gap: 2,
                            display: 'flex',
                            borderRadius: 3,
                            alignItems: 'center',
                            bgcolor: alpha(theme.palette.error.main, 0.15),
                            animation: 'alertPulse 2s ease-in-out infinite',
                            border: `2px solid ${alpha(theme.palette.error.main, 0.4)}`,
                            boxShadow: `0 0 60px ${alpha(theme.palette.error.main, 0.15)}`,
                            '@keyframes alertPulse': {
                                '0%, 100%': { borderColor: alpha(theme.palette.error.main, 0.4) },
                                '50%': { borderColor: alpha(theme.palette.error.main, 0.8) },
                            }
                        }}
                    >
                        <ErrorOutline sx={{ fontSize: 36, color: theme.palette.error.main }} />
                        <Box>
                            <Typography variant="h6" sx={{ color: theme.palette.error.main, fontWeight: 800 }}>
                                {t('PAYMENT_STATUS_CARD_SYSTEM_BLOCKED')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.error.light, fontWeight: 600 }}>
                                {t('PAYMENT_STATUS_CARD_SYSTEM_BLOCKED_MSG', {
                                    days: Math.abs(daysUntilBlock!),
                                    plural: Math.abs(daysUntilBlock!) > 1 ? 's' : ''
                                })}
                                {payment.link && t('PAYMENT_STATUS_CARD_SYSTEM_BLOCKED_ACTION')}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {isExpiringSoon && !isBlocked && (
                    <Box
                        sx={{
                            p: 2.5,
                            mt: 2.5,
                            gap: 2,
                            borderRadius: 3,
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: alpha(theme.palette.warning.main, 0.12),
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                            boxShadow: `0 0 40px ${alpha(theme.palette.warning.main, 0.08)}`,
                        }}
                    >
                        <Warning sx={{ fontSize: 28, color: theme.palette.warning.main }} />
                        <Box>
                            <Typography variant="body1" sx={{ color: theme.palette.warning.main, fontWeight: 700 }}>
                                {t('PAYMENT_STATUS_CARD_EXPIRING_SOON')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.warning.light, fontWeight: 600 }}>
                                {t('PAYMENT_STATUS_CARD_EXPIRING_MSG', {
                                    days: daysUntilBlock,
                                    plural: daysUntilBlock > 1 ? 's' : ''
                                })}
                                {payment.link && t('PAYMENT_STATUS_CARD_EXPIRING_ACTION')}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}